import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import Plot from 'react-plotly.js';
import DataTable from 'react-data-table-component';
import './App.css';
import { calculateDaysUntilNextPayment } from './DebtFunctions';
import { formatCurrency, formatRate } from './CoreFunctions';

const FourOhOneKLoanDetail = () => {
    const { id } = useParams();
    const [loan, setLoan] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showRemainingOnly, setShowRemainingOnly] = useState(true);
    const [amortizationSchedule, setAmortizationSchedule] = useState([]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await axios.get(`/api/balance_sheet/financing/401k_loans/${id}/`);
                console.log('API Response:', response.data);
                setLoan(response.data);

                const scheduleData = (response.data.amortization_schedule || []).map(entry => ({
                    id: entry.id,
                    payment_date: entry.payment_date,
                    loan_payment_amount: parseFloat(entry.loan_payment_amount),
                    principal_payment_amount: parseFloat(entry.principal_payment_amount),
                    interest_payment_amount: parseFloat(entry.interest_payment_amount),
                    outstanding_balance: parseFloat(entry.outstanding_balance),
                    percent_outstanding: parseFloat(entry.percent_outstanding),
                    cumulative_recurring_payment_amt: parseFloat(entry.cumulative_recurring_payment_amt),
                    cumulative_principal_payment_amt: parseFloat(entry.cumulative_principal_payment_amt),
                    cumulative_interest_payment_amt: parseFloat(entry.cumulative_interest_payment_amt),
                }));

                setAmortizationSchedule(scheduleData);
                setLoading(false);
            } catch (error) {
                console.error('Error fetching 401k loan details:', error);
                setLoading(false);
            }
        };
        fetchData();
    }, [id]);

    let daysUntilPayment = null;
    if (loan && loan.upcoming_payment) {
        daysUntilPayment = calculateDaysUntilNextPayment(loan.upcoming_payment.payment_date);
    }

    if (loading) return <div>Loading 401k loan details...</div>;
    if (!loan) return <div>No 401k loan found!</div>;

    const filteredSchedule = showRemainingOnly
        ? amortizationSchedule.filter(row => new Date(row.payment_date) > new Date())
        : amortizationSchedule;

    const xData = filteredSchedule.map(row => new Date(row.payment_date));
    const principalPayments = filteredSchedule.map(row => parseFloat(row.principal_payment_amount) || 0);
    const interestPayments = filteredSchedule.map(row => parseFloat(row.interest_payment_amount) || 0);

    const columns = [
        { name: 'Payment Date', selector: row => row.payment_date, sortable: true },
        { name: 'Payment Amount', selector: row => `$${parseFloat(row.loan_payment_amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, sortable: true },
        { name: 'Principal', selector: row => `$${parseFloat(row.principal_payment_amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, sortable: true },
        { name: 'Interest', selector: row => `$${parseFloat(row.interest_payment_amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, sortable: true },
        { name: 'Outstanding Balance', selector: row => `$${parseFloat(row.outstanding_balance).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, sortable: true },
        { name: 'Remaining %', selector: row => `${(parseFloat(row.percent_outstanding) * 100).toFixed(1)}%`, sortable: true },
        { name: 'Cumulative Payments', selector: row => `$${parseFloat(row.cumulative_recurring_payment_amt).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, sortable: true },
        { name: 'Cumulative Interest', selector: row => `$${parseFloat(row.cumulative_interest_payment_amt).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, sortable: true },
        { name: 'Cumulative Principal', selector: row => `$${parseFloat(row.cumulative_principal_payment_amt).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, sortable: true },
    ];

    const dataTableStyles = {
        table: { style: { backgroundColor: '#111827' } },
        headRow: { style: { backgroundColor: '#1f2937', color: '#e5e7eb', borderBottom: '1px solid #374151' } },
        headCells: { style: { color: '#e5e7eb' } },
        rows: {
            style: { backgroundColor: '#111827', color: '#e5e7eb', borderBottom: '1px solid #374151' },
            highlightOnHoverStyle: { backgroundColor: '#0b1220', color: '#e5e7eb' },
        },
        pagination: {
            style: { backgroundColor: '#111827', color: '#e5e7eb', borderTop: '1px solid #374151' },
            pageButtonsStyle: { fill: '#e5e7eb', color: '#e5e7eb' },
        },
    };

    const conditionalRowStyles = [
        { when: (_row, index) => index % 2 === 1, style: { backgroundColor: '#0f172a' } },
    ];

    return (
        <div>
            <h1>401k Loan Details: {loan.plan_name || loan.lender}</h1>

            <div className="top-section">
                <div className="loan-stats" style={{ backgroundColor: '#1f2937', color: '#e5e7eb', borderColor: '#374151', minWidth: '350px' }}>
                    <p><strong>Upcoming Payment: </strong></p>
                    {loan.upcoming_payment ? (
                        <>
                            <p style={{ marginLeft: '30px' }}>Date: {loan.upcoming_payment.payment_date} (in {daysUntilPayment} days)</p>
                            <p style={{ marginLeft: '30px' }}>Amount: {formatCurrency(loan.upcoming_payment.loan_payment_amount)}</p>
                            <p style={{ marginLeft: '30px' }}>Rate: {formatRate(loan.initial_interest_rate / 100, 2)}</p>
                        </>
                    ) : (
                        <p style={{ marginLeft: '30px' }}>No upcoming payment available.</p>
                    )}
                    <p><strong>Loan Stats: </strong></p>
                    {loan.recent_payment ? (
                        <p style={{ marginLeft: '30px' }}>
                            Outstanding: {formatCurrency(loan.recent_payment.outstanding_balance)} ({(parseFloat(loan.recent_payment.percent_outstanding) * 100).toFixed(1)}%)
                        </p>
                    ) : (
                        <p style={{ marginLeft: '30px' }}>No recent payment data available.</p>
                    )}
                    <p><strong>Plan Name: </strong>{loan.plan_name}</p>
                    <p><strong>Payments Per Year: </strong>{loan.n_payments_per_year} (bi-weekly)</p>
                </div>

                <div className="plot-container" style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Plot
                        data={[
                            { x: xData, y: principalPayments, type: 'scatter', mode: 'lines+markers', name: 'Principal Payments' },
                            { x: xData, y: interestPayments, type: 'scatter', mode: 'lines+markers', name: 'Interest Payments' },
                        ]}
                        layout={{
                            template: 'plotly_dark',
                            paper_bgcolor: 'rgba(0,0,0,0)',
                            plot_bgcolor: 'rgba(0,0,0,0)',
                            width: 800,
                            height: 450,
                            title: { text: 'Amortization Schedule', font: { weight: 'bold' } },
                            xaxis: { title: 'Payment Date' },
                            yaxis: { title: { text: 'Payment Amount', standoff: 20 } },
                            legend: {
                                orientation: 'h', x: 0.5, xanchor: 'center', y: -0.3, yanchor: 'bottom',
                                bgcolor: 'rgba(17, 24, 39, 0.7)', bordercolor: '#374151', borderwidth: 1,
                            },
                        }}
                        config={{ responsive: true }}
                    />
                </div>
            </div>

            <div style={{ marginLeft: '40px' }}>
                <label>
                    <input
                        type="checkbox"
                        checked={showRemainingOnly}
                        onChange={() => setShowRemainingOnly(!showRemainingOnly)}
                    />
                    {' '}Show only remaining payments
                </label>
            </div>

            <div className="data-table-container">
                <DataTable
                    title="Amortization Schedule Table"
                    columns={columns}
                    data={filteredSchedule}
                    pagination
                    paginationPerPage={50}
                    striped={false}
                    highlightOnHover
                    customStyles={dataTableStyles}
                    conditionalRowStyles={conditionalRowStyles}
                    persistTableHead
                />
            </div>
        </div>
    );
};

export default FourOhOneKLoanDetail;
